import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useUiStore } from '@/stores/uiStore';
import type { NotificationDto } from '@forest/contracts';
import {
  useMarkAllRead,
  useMarkRead,
  useNotifications,
  useUnreadCount,
} from '@/features/notifications/useNotifications';
import {
  notificationIcon,
  relativeTime,
  renderNotificationText,
} from '@/features/notifications/render';

export function NotificationBell() {
  const { t, i18n } = useTranslation(['notifications', 'common']);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  // Start fixed + hidden so the panel can never render in normal document
  // flow (which previously flashed it at the top-left, clipped, before the
  // positioning effect ran). useLayoutEffect fills in the coords + reveals
  // it before the browser paints.
  const [style, setStyle] = useState<React.CSSProperties>({
    position: 'fixed',
    visibility: 'hidden',
  });

  const pushToast = useUiStore((s) => s.pushToast);
  const unreadQ = useUnreadCount();
  const count = unreadQ.data ?? 0;

  // Non-spammy "new notification" toast: only when the unread count rises
  // between polls (and not on the very first load, where prev is undefined).
  const prevCountRef = useRef<number | null>(null);
  useEffect(() => {
    if (unreadQ.data === undefined) return;
    const prev = prevCountRef.current;
    if (prev !== null && unreadQ.data > prev) {
      pushToast({
        variant: 'info',
        message: t('notifications:bell.newToast', {
          count: unreadQ.data - prev,
          defaultValue: '{{count}} notificare nouă',
        }),
      });
    }
    prevCountRef.current = unreadQ.data;
  }, [unreadQ.data, pushToast, t]);

  // Only fetch the dropdown list while the panel is open.
  const listQ = useNotifications('all', 8);
  const items: NotificationDto[] = open
    ? (listQ.data?.pages.flatMap((p) => p.items) ?? []).slice(0, 8)
    : [];

  const markRead = useMarkRead();
  const markAllRead = useMarkAllRead();

  // Position the panel under the trigger (fixed → escapes header overflow).
  // useLayoutEffect runs before paint, so the panel is never visible in a
  // mispositioned state. Re-anchors on scroll/resize while open so it tracks
  // the bell (mirrors the MultiSelect pattern, CLAUDE.md rule 33).
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const el = triggerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const width = Math.min(360, window.innerWidth - 16);
      const left = Math.max(8, Math.min(rect.right - width, window.innerWidth - width - 8));
      const top = Math.max(8, Math.min(rect.bottom + 8, window.innerHeight - 16));
      setStyle({ position: 'fixed', zIndex: 9999, top, left, width, visibility: 'visible' });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (panelRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const onItemClick = (n: NotificationDto) => {
    if (!n.read) markRead.mutate(n.id);
    setOpen(false);
    if (n.url) navigate(n.url);
  };

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'relative rounded-md p-2 transition-colors',
          count > 0
            ? 'bg-red-50 text-red-600 hover:bg-red-100 dark:bg-red-500/10 dark:text-red-300 dark:hover:bg-red-500/20'
            : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200',
        )}
        aria-label={t('notifications:bell.aria', { defaultValue: 'Notificări' })}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Bell className={cn('h-5 w-5', count > 0 && 'animate-[swing_1s_ease-in-out] fill-red-500/15')} />
        {count > 0 ? (
          <span className="absolute -right-1 -top-1 inline-flex" aria-hidden>
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
            <span className="relative inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold leading-none text-white ring-2 ring-white dark:ring-slate-900">
              {count > 99 ? '99+' : count}
            </span>
          </span>
        ) : null}
      </button>

      {open ? (
        <div
          ref={panelRef}
          style={style}
          className="max-h-[70vh] overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-800 dark:shadow-[0_8px_24px_rgba(0,0,0,0.4)]"
          role="menu"
        >
          <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2 dark:border-slate-700">
            <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">
              {t('notifications:bell.title', { defaultValue: 'Notificări' })}
            </span>
            {count > 0 ? (
              <button
                type="button"
                onClick={() => markAllRead.mutate()}
                disabled={markAllRead.isPending}
                className="inline-flex items-center gap-1 text-xs font-medium text-forest-700 hover:underline disabled:opacity-50 dark:text-forest-300"
              >
                <CheckCheck className="h-3.5 w-3.5" />
                {t('notifications:bell.markAll', { defaultValue: 'Marchează toate' })}
              </button>
            ) : null}
          </div>

          <div className="max-h-[50vh] overflow-y-auto">
            {listQ.isLoading ? (
              <div className="px-3 py-6 text-center text-sm text-slate-500 dark:text-slate-400">
                {t('common:loading', { defaultValue: 'Se încarcă…' })}
              </div>
            ) : items.length === 0 ? (
              <div className="px-3 py-8 text-center text-sm text-slate-500 dark:text-slate-400">
                {t('notifications:empty.title', { defaultValue: 'Nicio notificare' })}
              </div>
            ) : (
              <ul className="divide-y divide-slate-100 dark:divide-slate-700/60">
                {items.map((n) => {
                  const { title, body } = renderNotificationText(n, t);
                  const Icon = notificationIcon(n.type);
                  return (
                    <li key={n.id}>
                      <button
                        type="button"
                        onClick={() => onItemClick(n)}
                        className={cn(
                          'flex w-full items-start gap-2.5 px-3 py-2.5 text-left transition-colors hover:bg-slate-50 dark:hover:bg-slate-700/50',
                          !n.read && 'bg-forest-50/40 dark:bg-forest-900/10',
                        )}
                      >
                        <span className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-300">
                          <Icon className="h-3.5 w-3.5" />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="flex items-center gap-1.5">
                            {!n.read ? (
                              <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-forest-500" aria-hidden />
                            ) : null}
                            <span
                              className={cn(
                                'truncate text-sm',
                                n.read
                                  ? 'font-medium text-slate-700 dark:text-slate-300'
                                  : 'font-semibold text-slate-900 dark:text-slate-100',
                              )}
                            >
                              {title}
                            </span>
                          </span>
                          {body ? (
                            <span className="mt-0.5 line-clamp-2 block text-xs text-slate-500 dark:text-slate-400">
                              {body}
                            </span>
                          ) : null}
                          <span className="mt-0.5 block text-[10px] text-slate-400 dark:text-slate-500">
                            {relativeTime(n.createdAt, t, i18n.language)}
                          </span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="border-t border-slate-100 px-3 py-2 dark:border-slate-700">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                navigate('/app/notifications');
              }}
              className="w-full rounded-md py-1.5 text-center text-xs font-semibold text-forest-700 transition-colors hover:bg-forest-50 dark:text-forest-300 dark:hover:bg-forest-900/20"
            >
              {t('notifications:bell.seeAll', { defaultValue: 'Vezi toate' })}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
